import React from 'react'
import { Autocomplete, TextField, Paper } from '@mui/material'
import { styled } from '@mui/material/styles'

const ContainerWrapper = styled('div')(({ error, width }) => ({
  borderRadius: '4px 4px 0 0',
  backgroundColor: '#E5E7FF',
  padding: '12px 12px 0 12px',
  width: width || '100%',
  borderBottom: error ? '2px solid #E53935' : '2px solid #5952E0BF',
  position: 'relative'
}))

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#E5E7FF',
    borderRadius: 0,
    padding: '0',
    paddingLeft: '0 !important',
    paddingRight: '0',
    alignItems: 'flex-start',
    '& fieldset': {
      border: 'none',
      padding: 0
    },
    '&:hover fieldset': {
      border: 'none'
    },
    '&.Mui-focused fieldset': {
      border: 'none'
    }
  },
  '& .MuiInputBase-input': {
    color: 'var(--finago-black)',
    fontSize: '13px',
    fontFamily: 'Borna',
    fontWeight: '500',
    padding: '2px 0',
    paddingLeft: '0 !important',
    paddingRight: '0',
    marginLeft: '0',
    '&::placeholder': {
      color: 'var(--finago-black)',
      opacity: 1,
      fontSize: '13px',
      fontWeight: '500',
      lineHeight: '1.2'
    }
  },
  '& .MuiInputLabel-root': {
    color: 'var(--finago-black)',
    fontSize: '10px',
    fontWeight: '600',
    fontFamily: 'Borna',
    lineHeight: '1.2',
    transform: 'translate(0, -6px) scale(1)',
    top: '0',
    left: '0',
    marginLeft: '0',
    paddingLeft: '0',
    '&.Mui-focused': {
      color: 'var(--finago-black)',
      transform: 'translate(0, -6px) scale(1)'
    },
    '&.MuiInputLabel-shrink': {
      transform: 'translate(0, -6px) scale(1)',
      top: '0',
      left: '0',
      marginLeft: '0'
    }
  },
  '& .MuiInputBase-root': {
    paddingTop: '0',
    paddingLeft: '0',
    paddingRight: '0',
    marginTop: '0',
    marginLeft: '0',
    alignItems: 'baseline'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    padding: 0
  }
})

const StyledPaper = styled(Paper)({
  width: 'calc(100% + 24px)',
  marginLeft: '-12px',
  marginTop: '1px',
  backgroundColor: '#E5E7FF',
  border: '1px solid #D4E8F7',
  borderRadius: 0
})

const listboxStyles = {
  maxHeight: '200px',
  padding: 0,
  '& .MuiAutocomplete-option': {
    color: 'var(--finago-black)',
    fontSize: '13px',
    fontFamily: 'Borna',
    padding: '8px 12px',
    '&:hover': {
      backgroundColor: '#F0F8FF'
    },
    '&[aria-selected="true"]': {
      backgroundColor: '#D4E8F7',
      '&:hover': {
        backgroundColor: '#D4E8F7'
      }
    }
  }
}

const helperTextStyles = {
  color: '#E53935',
  fontSize: '11px',
  fontFamily: 'Borna',
  marginTop: '4px',
  marginLeft: 0
}

/** Customised mui autocomplete field built for calendar settings view */
const CustomAutocompleteField = React.forwardRef((props, ref) => {
  const {
    value,
    onChange,
    options = [],
    label,
    disabled = false,
    className = '',
    error = false,
    name,
    width = '100%',
    placeholder = 'Select an option...',
    inputProps = {},
    ...restProps
  } = props

  const getErrorMessage = () => {
    if (typeof error === 'string') return error
    if (error) return 'Invalid selection'
    return null
  }

  const errorMessage = getErrorMessage()
  const errorId = name ? `${name}-error` : undefined
  const labelId = name ? `${name}-label` : undefined
  const describedById = errorMessage ? errorId : labelId

  return (
    <ContainerWrapper className={className} error={error} width={width}>
      <Autocomplete
        ref={ref}
        value={value}
        onChange={(_, newValue) => {
          if (typeof onChange === 'function') {
            onChange(newValue)
          }
        }}
        options={options}
        getOptionLabel={(option) => option?.label ?? ''}
        isOptionEqualToValue={(option, val) => option?.value === val?.value}
        disabled={disabled}
        clearOnBlur={false}
        selectOnFocus
        handleHomeEndKeys
        renderInput={(params) => (
          <StyledTextField
            {...params}
            name={name}
            label={label}
            placeholder={placeholder}
            error={!!error}
            helperText={errorMessage}
            slotProps={{
              ...params.slotProps,
              formHelperText: {
                id: errorId,
                sx: helperTextStyles
              },
              inputLabel: {
                ...params.slotProps?.inputLabel,
                id: labelId,
                shrink: true
              }
            }}
            {...inputProps}
          />
        )}
        slots={{
          paper: StyledPaper
        }}
        slotProps={{
          popper: {
            disablePortal: true,
            placement: 'bottom-start',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 1]
                }
              }
            ]
          },
          listbox: {
            sx: listboxStyles
          }
        }}
        aria-label={label || placeholder}
        aria-describedby={describedById}
        aria-invalid={!!error}
        {...restProps}
      />
    </ContainerWrapper>
  )
})

CustomAutocompleteField.displayName = 'CustomAutocompleteField'

export default CustomAutocompleteField
